import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import useEffectOnce from 'hooks/useEffectOnce';
import Button from 'components/common/Button';
import ColorSwatchWrapper from 'components/search/ColorSwatches';
import { track } from 'apis/amethyst';
import { evProductCardInteract, LAUNCH_CALENDAR_FLOW_TYPE } from 'events/launchCalendar';
import useWindowSize from 'hooks/useWindowSize';
import type { Product } from 'constants/searchTypes';
import { LaunchCalendarNotifyMeModal } from 'components/launchcalendar/LaunchCalendarNotifyMeModal';
import UtilityStrokeNotificationMediumIcon from 'tailwind/components/Icons/UtilityStrokeNotificationMediumIcon';
import UtilityStrokeNotificationSmallIcon from 'tailwind/components/Icons/UtilityStrokeNotificationSmallIcon';
import type { ProductWithRelations } from 'types/calypso';
import type { LaunchCalendarProductInfo } from 'types/launchCalendar';
import { LaunchCalendarSectionType } from 'types/launchCalendar';
import useMartyContext from 'hooks/useMartyContext';
import { formatEpochToESTDate, translateToProducts } from 'components/launchcalendar/LaunchCalendar.utility';
import UpcomingStyleQuickViewModal from 'components/launchcalendar/UpcomingStyleQuickViewModal';
import { selectLaunchCalendar } from 'selectors/launchCalendar';
import ProductUtils from 'helpers/ProductUtils';
import { isDesktopViewport, isMobileViewport } from 'utils/viewportUtil';

import css from 'styles/components/launchcalendar/launchCalendarSection.scss';
// eslint-disable-next-line css-modules/no-unused-class
import cardCSS from 'styles/components/common/productCard.scss';

interface LaunchCalendarSectionProps {
  heading: string;
  sectionType: LaunchCalendarSectionType;
  products: LaunchCalendarProductInfo[];
}

interface StyleStatus {
  productId: number | undefined;
  styleId: number | undefined;
  isItemSetupCompleted: boolean;
}

interface ProductStyles {
  [key: number]: number | undefined;
}

export const LaunchCalendarSection = (props: LaunchCalendarSectionProps) => {
  const { testId } = useMartyContext();

  const { heading, sectionType, products } = props;
  const isTodaysReleasesSection = sectionType === LaunchCalendarSectionType.TODAYS_RELEASES;
  const isRecentLaunchesSection = sectionType === LaunchCalendarSectionType.RECENT_LAUNCHES;

  const [isLCNotifyMeModalOpen, setLCNotifyMeModalOpen] = useState(false);
  const [productStyles, setProductStyles] = useState<ProductStyles>({});
  const [styleStatus, setStyleStatus] = useState<StyleStatus>({
    productId: undefined,
    styleId: undefined,
    isItemSetupCompleted: false
  });
  const [isQuickViewModalOpen, setIsQuickViewModalOpen] = useState(false);
  const { upcomingStylesQuickViewData } = useSelector(selectLaunchCalendar) || {};
  const { width: windowWidth = 0 } = useWindowSize();
  const isDesktop = isDesktopViewport(windowWidth);
  const isMobile = isMobileViewport(windowWidth);

  const addProductStyles = (productId: number, styleId?: number) => {
    if (styleId) {
      setProductStyles(prevState => ({
        ...prevState,
        [productId]: styleId
      }));
    }
  };

  const handleNotifyMeClick = (productId: number, isItemSetupCompletedForNotifyMeStyle: boolean) => {
    setStyleStatus({ productId, styleId: productStyles[productId], isItemSetupCompleted: isItemSetupCompletedForNotifyMeStyle });
    setLCNotifyMeModalOpen(true);
    track(() => [evProductCardInteract, { flowType: LAUNCH_CALENDAR_FLOW_TYPE.NotifyMeModal, styleId: productStyles[productId] }]);
  };

  const closeQuickViewModal = () => {
    setIsQuickViewModalOpen(false);
    setStyleStatus({ productId: undefined, styleId: undefined, isItemSetupCompleted: false });
  };

  const productCardClick = (
    e: React.MouseEvent<HTMLAnchorElement> | React.KeyboardEvent<HTMLAnchorElement>,
    productId: number,
    styleId?: number
  ) => {
    const newStyleId = styleId ?? productStyles[productId]!;

    if (isRecentLaunchesSection) {
      return;
    }
    if (isTodaysReleasesSection) {
      const productData = upcomingStylesQuickViewData[productId];
      const styles = productData?.detail?.styles;
      if (styles) {
        const style = ProductUtils.getStyleMap(styles)[newStyleId];
        if (style && ProductUtils.hasAvailableStock(style)) {
          track(() => [evProductCardInteract, { flowType: LAUNCH_CALENDAR_FLOW_TYPE.ProductDetailPage, styleId: newStyleId }]);
          return; // continue with default action of redirecting
        }
      }
    }

    e.preventDefault();
    setStyleStatus({ productId, styleId: newStyleId, isItemSetupCompleted: true });
    setIsQuickViewModalOpen(true);
    track(() => [evProductCardInteract, { flowType: LAUNCH_CALENDAR_FLOW_TYPE.UpcomingStyleModal, styleId: newStyleId }]);
  };

  const handleProductCardClick = (
    e: React.MouseEvent<HTMLAnchorElement> | React.KeyboardEvent<HTMLAnchorElement>,
    productId: number,
    styleId?: number
  ) => {
    productCardClick(e, productId, styleId);
    addProductStyles(productId, styleId);
  };

  const handleSwatchClick = (focusedProduct: Product | ProductWithRelations) => {
    const { productId, styleId } = focusedProduct;
    addProductStyles(Number(productId), Number(styleId));
  };

  useEffectOnce(() => {
    products.map(product => {
      addProductStyles(product.productId, product.relatedStyles[0]!.styleId);
    });
  });

  const getRelatedStyleIds = (products: LaunchCalendarProductInfo[], productId: number): number[] =>
    products
      .filter(product => product.productId === productId)
      .map(product => product.relatedStyles.map(relatedStyle => relatedStyle.styleId))
      .flat();
  return (
    <div className={css.mainCard}>
      <div className={css.heading_section}>
        <h2 className={css.title}>{heading}</h2>
        {!isTodaysReleasesSection && <header>({products.length} items)</header>}
      </div>
      <div className={css.products}>
        {products.map(product => {
          const {
            productId,
            relatedStyles: [{ styleId = 0, imageId = '', hardLaunchDate = 0 } = {}],
            relatedStyles
          } = product;

          const productCardProps = {
            shouldLazyLoad: true,
            showPrice: true,
            showColorName: false,
            showRatingStars: false,
            isLaunchCalendarView: true,
            dateLabel: formatEpochToESTDate({ epoch: hardLaunchDate })
          };

          const heartsInfo = {
            heartsData: { showFavoriteHeart: false },
            styleId: String(styleId),
            productId: String(productId)
          };

          return (
            <div className={cardCSS.launchCalendarCard} key={styleId}>
              <ColorSwatchWrapper
                {...translateToProducts(relatedStyles)[0]!}
                productCardProps={productCardProps}
                heartsInfo={heartsInfo}
                relatedStyles={translateToProducts(relatedStyles)}
                key={styleId}
                eventLabel="LaunchCalendar"
                testId={testId('launchCalendarSection')}
                onClick={e => handleProductCardClick(e, productId, styleId)}
                onSwatchClickHandler={handleSwatchClick}
              />
              {!isTodaysReleasesSection && !isRecentLaunchesSection && (
                <Button
                  variant={'filled'}
                  size={isDesktop ? 'base' : 'small'}
                  leadIcon={isMobile ? <UtilityStrokeNotificationSmallIcon size={16} /> : <UtilityStrokeNotificationMediumIcon size={24} />}
                  onClick={() => handleNotifyMeClick(product.productId, !!imageId)}
                >
                  Notify Me
                </Button>
              )}
            </div>
          );
        })}
        {isLCNotifyMeModalOpen && styleStatus.styleId && (
          <LaunchCalendarNotifyMeModal
            heading="Notify Me"
            styleId={styleStatus.styleId}
            contentLabel="Launch Calendar Notify Me"
            isOpen={isLCNotifyMeModalOpen}
            isItemSetupCompleted={styleStatus.isItemSetupCompleted}
            setLCNotifyMeModalOpen={setLCNotifyMeModalOpen}
          />
        )}
        {isQuickViewModalOpen && (
          <UpcomingStyleQuickViewModal
            styleId={String(styleStatus.styleId)}
            productId={String(styleStatus.productId)}
            relatedStyleIds={getRelatedStyleIds(products, styleStatus.productId!)}
            closeModal={closeQuickViewModal}
            showNotifyMe={!isTodaysReleasesSection}
          />
        )}
      </div>
    </div>
  );
};

export default LaunchCalendarSection;
